// src/pages/storePages/manageStore/ManageStoreProducts.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  storeGetAllProducts,
  storeDeleteProduct,
} from "../../../storeAPIs/StoreProductsAPIs";
import DeleteProductDialog from "./DeleteProductDialog";
import "../../../static/storecss/manageStore/ManageStoreProducts.css";

const STOCK_FILTERS = [
  { id: "all", label: "All" },
  { id: "in", label: "In stock" },
  { id: "low", label: "Low stock" },
  { id: "out", label: "Out of stock" },
];

const VISIBILITY_FILTERS = [
  { id: "all", label: "Any" },
  { id: "true", label: "Active" },
  { id: "false", label: "Hidden" },
];

const SORT_OPTIONS = [
  { id: "-created_at", label: "Newest" },
  { id: "created_at", label: "Oldest" },
  { id: "name", label: "Name A–Z" },
  { id: "-name", label: "Name Z–A" },
  { id: "price", label: "Price ↑" },
  { id: "-price", label: "Price ↓" },
  { id: "stock", label: "Stock ↑" },
  { id: "-stock", label: "Stock ↓" },
];

const PAGE_SIZE = 20;

export default function ManageStoreProducts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL is the source of truth ──
  const page = parseInt(searchParams.get("page") || "1", 10);
  const search = searchParams.get("search") || "";
  const stock = searchParams.get("stock") || "all";
  const visibility = searchParams.get("is_active") || "all";
  const ordering = searchParams.get("ordering") || "-created_at";

  // Local input mirrors the URL's search — debounced back into the URL
  const [searchInput, setSearchInput] = useState(search);
  const debounceRef = useRef(null);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParam("search", value);
    }, 300);
  };

  // ── Data state ──
  const [products, setProducts] = useState([]);
  const [count, setCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 👇 Delete-dialog state
  const [pendingDelete, setPendingDelete] = useState(null);

  // ── Fetch whenever any URL param changes ──
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = { page, page_size: PAGE_SIZE };
    if (search) params.search = search;
    if (stock !== "all") params.stock = stock;
    if (visibility !== "all") params.is_active = visibility;
    if (ordering) params.ordering = ordering;

    storeGetAllProducts(params)
      .then((res) => {
        if (cancelled) return;
        const data = res.data ?? {};
        setProducts(data.results ?? (Array.isArray(data) ? data : []));
        setCount(
          data.count ??
            (data.results?.length || (Array.isArray(data) ? data.length : 0))
        );
        setHasNext(Boolean(data.next));
        setHasPrev(Boolean(data.previous));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load products.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, search, stock, visibility, ordering]);

  // 👇 Confirmed delete handler (called by the dialog)
  const handleConfirmDelete = async (product) => {
    try {
      await storeDeleteProduct(product.slug);
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
      setCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      alert(err.response?.data?.detail || err.message || "Failed to delete.");
      throw err;
    }
  };

  const goToPage = (n) => updateParam("page", String(n));

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / PAGE_SIZE)),
    [count]
  );

  const hasAnyFilter =
    search ||
    stock !== "all" ||
    visibility !== "all" ||
    ordering !== "-created_at";

  const clearAllFilters = () => {
    setSearchParams({}, { replace: true });
    setSearchInput("");
  };

  return (
    <div className="msp-page">
      <header className="msp-header">
        <div>
          <h1>Products</h1>
          <p>
            {loading && products.length === 0
              ? "Loading…"
              : `${count} product${count === 1 ? "" : "s"} found`}
          </p>
        </div>
        <Link to="/manage-store/product/new" className="msp-header-cta">
          <i className="fa-solid fa-plus" />
          New product
        </Link>
      </header>

      <div className="msp-toolbar">
        <div className="msp-search">
          <i className="fa-solid fa-magnifying-glass" />
          <input
            type="text"
            placeholder="Search name, slug, category…"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchInput && (
            <button
              type="button"
              className="msp-search-clear"
              onClick={() => handleSearchChange("")}
              aria-label="Clear search"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>

        <div className="msp-filters">
          {STOCK_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`msp-filter ${stock === f.id ? "is-active" : ""}`}
              onClick={() => updateParam("stock", f.id === "all" ? "" : f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <select
          className="msp-select"
          value={visibility}
          onChange={(e) =>
            updateParam(
              "is_active",
              e.target.value === "all" ? "" : e.target.value
            )
          }
          aria-label="Visibility"
        >
          {VISIBILITY_FILTERS.map((v) => (
            <option key={v.id} value={v.id}>
              {v.label}
            </option>
          ))}
        </select>

        <select
          className="msp-select"
          value={ordering}
          onChange={(e) => updateParam("ordering", e.target.value)}
          aria-label="Sort by"
        >
          {SORT_OPTIONS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>

        {hasAnyFilter && (
          <button
            type="button"
            className="msp-clear-filters"
            onClick={clearAllFilters}
          >
            Clear filters
          </button>
        )}
      </div>

      {error && <div className="msp-alert">{error}</div>}

      {loading && products.length === 0 ? (
        <div className="msp-loading">
          <span className="msp-spinner" />
          Loading products…
        </div>
      ) : products.length === 0 ? (
        <div className="msp-empty">
          <i className="fa-solid fa-box-open" />
          <h2>{hasAnyFilter ? "No matches" : "No products yet"}</h2>
          <p>
            {hasAnyFilter
              ? "Try a different filter or search."
              : "Create your first product to start selling."}
          </p>
          {hasAnyFilter ? (
            <button
              type="button"
              className="msp-btn msp-btn--primary"
              onClick={clearAllFilters}
            >
              Clear filters
            </button>
          ) : (
            <Link
              to="/manage-store/product/new"
              className="msp-btn msp-btn--primary"
            >
              <i className="fa-solid fa-plus" /> New product
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className={`msp-table-wrap ${loading ? "is-loading" : ""}`}>
            <table className="msp-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Product</th>
                  <th>Category</th>
                  <th className="msp-th-right">Price</th>
                  <th className="msp-th-right">Stock</th>
                  <th>Status</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const low = p.stock > 0 && p.stock <= 5;
                  const out = p.stock <= 0;
                  return (
                    <tr key={p.id}>
                      <td className="msp-thumb-cell">
                        <div className="msp-thumb">
                          {p.primary_image ? (
                            <img
                              src={p.primary_image}
                              alt={p.name}
                              loading="lazy"
                            />
                          ) : (
                            <i className="fa-solid fa-image" />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="msp-name">
                          <strong>{p.name}</strong>
                          <code>{p.slug}</code>
                        </div>
                      </td>
                      <td className="msp-muted">
                        {p.category?.name ?? "—"}
                      </td>
                      <td className="msp-price">
                        KES {Number(p.price).toLocaleString()}
                      </td>
                      <td className="msp-stock">
                        <span
                          className={`msp-stock-pill ${
                            out ? "is-out" : low ? "is-low" : "is-in"
                          }`}
                        >
                          {out ? "0" : p.stock}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`msp-visibility ${
                            p.is_active ? "is-active" : "is-hidden"
                          }`}
                        >
                          {p.is_active ? "Active" : "Hidden"}
                        </span>
                      </td>
                      <td className="msp-actions">
                        <Link
                          to={`/store/product/${p.slug}`}
                          className="msp-btn msp-btn--icon"
                          title="View on storefront"
                        >
                          <i className="fa-solid fa-eye" />
                        </Link>
                        <button
                          type="button"
                          className="msp-btn msp-btn--icon"
                          title="Edit"
                          onClick={() =>
                            navigate(
                              `/manage-store/product/${
                                p.slug
                              }/edit?returnTo=${encodeURIComponent(
                                `/manage-store/products${
                                  searchParams.toString()
                                    ? `?${searchParams.toString()}`
                                    : ""
                                }`
                              )}`
                            )
                          }
                        >
                          <i className="fa-solid fa-pen" />
                        </button>
                        <button
                          type="button"
                          className="msp-btn msp-btn--icon msp-btn--danger"
                          title="Delete"
                          onClick={() => setPendingDelete(p)}
                        >
                          <i className="fa-solid fa-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(hasNext || hasPrev || totalPages > 1) && (
            <div className="msp-pagination">
              <button
                type="button"
                className="msp-page-btn"
                onClick={() => goToPage(page - 1)}
                disabled={!hasPrev || loading}
              >
                <i className="fa-solid fa-chevron-left" /> Previous
              </button>

              <span className="msp-page-status">
                Page <strong>{page}</strong>
                {totalPages > 1 && ` of ${totalPages}`}
              </span>

              <button
                type="button"
                className="msp-page-btn"
                onClick={() => goToPage(page + 1)}
                disabled={!hasNext || loading}
              >
                Next <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          )}
        </>
      )}

      {/* 👇 Delete confirmation dialog */}
      <DeleteProductDialog
        open={!!pendingDelete}
        product={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}